import {
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Inbox,
  Loader2,
  Send,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { EmailDialogShell } from '@/Domain/components/Integration/Email/EmailDialogShell';
import { OptionalBadge } from '@/Domain/components/Integration/Email/OptionalBadge';
import {
  DOMAIN_DOCS_URL,
  EMAIL_NS,
  EMAIL_RE,
} from '@/Domain/components/Integration/Email/email.const';
import type { EmailIntegrationParams } from '@/Domain/components/Integration/Email/email.types';
import { CopyField } from '@/Domain/components/Integration/components/CopyField';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { TabItem, TabPanel, Tabs, TabsList } from '@/Domain/components/Shared/Tabs/Tabs';

interface EmailDomainDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  params: EmailIntegrationParams | undefined;
  replyAddress: string;
  onReplyAddressChange: (value: string) => void;
  onSaveReplyAddress: () => void;
  onClearReplyAddress: () => void;
  isSavingReplyAddress: boolean;
  onVerify: () => void;
  isVerifying: boolean;
  onAddressCopied: () => void;
}

export const EmailDomainDialog: React.FC<EmailDomainDialogProps> = ({
  open,
  onOpenChange,
  params,
  replyAddress,
  onReplyAddressChange,
  onSaveReplyAddress,
  onClearReplyAddress,
  isSavingReplyAddress,
  onVerify,
  isVerifying,
  onAddressCopied,
}) => {
  const { t } = useTranslation(['integration', 'integrations']);

  const forwardingStepsRaw = t(`${EMAIL_NS}.forwarding_steps`, { returnObjects: true });
  const forwardingSteps = Array.isArray(forwardingStepsRaw) ? (forwardingStepsRaw as string[]) : [];

  const dnsRecords = params?.dns_records ?? [];
  const savedAddress = params?.reply_from_address ?? '';
  const trimmedAddress = replyAddress.trim();
  const replyAddressValid = EMAIL_RE.test(trimmedAddress);
  // The backend trims and lowercases, so compare like for like or Save stays
  // enabled after saving an address typed with capitals or trailing space.
  const replyAddressDirty = trimmedAddress.toLowerCase() !== savedAddress.trim().toLowerCase();
  const isPublicDomain = Boolean(params?.is_public_domain);
  const isDomainAuthenticated = Boolean(params?.domain_authenticated);

  return (
    <EmailDialogShell
      open={open}
      onOpenChange={onOpenChange}
      title={t(`${EMAIL_NS}.domain_configure_title`)}
      subtitle={t(`${EMAIL_NS}.domain_subtitle`)}
      testId="email-domain-dialog"
      closeTestId="email-domain-dialog-close"
      doneTestId="email-domain-done-button"
    >
      <div className="space-y-6">
        <div className="space-y-4" data-testid="email-custom-address-section">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Label
                htmlFor="email-reply-address"
                className="text-sm font-semibold text-foreground"
              >
                {t(`${EMAIL_NS}.custom_address_title`)}
              </Label>
              <OptionalBadge data-testid="email-reply-address-optional" />
            </div>
            <p className="text-sm text-muted-foreground text-pretty">
              {t(`${EMAIL_NS}.custom_address_help`)}
            </p>
          </div>
          <div className="space-y-2">
            {/* Same row as the copy control above: value on the left, its action on the right. */}
            <div className="flex items-center gap-2">
              <Input
                id="email-reply-address"
                type="email"
                size="sm"
                value={replyAddress}
                placeholder={t(`${EMAIL_NS}.reply_address_placeholder`)}
                onChange={(e) => onReplyAddressChange(e.target.value)}
                className="flex-1"
                data-testid="email-reply-address-input"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={onSaveReplyAddress}
                disabled={
                  !replyAddressValid || !replyAddressDirty || isSavingReplyAddress || isVerifying
                }
                className="whitespace-nowrap"
                data-testid="email-reply-address-save"
              >
                {isSavingReplyAddress ? <Loader2 className="size-4 animate-spin" /> : null}
                {t(`${EMAIL_NS}.reply_address_save`)}
              </Button>
              {savedAddress ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={onClearReplyAddress}
                  disabled={isSavingReplyAddress || isVerifying}
                  className="whitespace-nowrap"
                  data-testid="email-reply-address-clear"
                >
                  <Trash2 className="size-4" />
                  {t(`${EMAIL_NS}.reply_address_clear`)}
                </Button>
              ) : null}
            </div>
            {trimmedAddress && !replyAddressValid ? (
              <p className="text-xs text-destructive" data-testid="email-reply-address-error">
                {t(`${EMAIL_NS}.reply_address_invalid`)}
              </p>
            ) : null}
          </div>
        </div>

        {/* One tab per direction: forwarding brings mail in, DNS lets replies go out. */}
        <Tabs defaultValue="receiving">
          <TabsList className="w-full">
            <TabItem value="receiving" className="flex-1" data-testid="email-receiving-tab">
              <Inbox className="size-4" />
              {t(`${EMAIL_NS}.receiving_tab`)}
            </TabItem>
            <TabItem value="sending" className="flex-1" data-testid="email-sending-tab">
              <Send className="size-4" />
              {t(`${EMAIL_NS}.sending_tab`)}
            </TabItem>
          </TabsList>

          <TabPanel
            value="receiving"
            className="space-y-4 pt-4"
            data-testid="email-receiving-section"
          >
            <p className="text-sm text-muted-foreground text-pretty">
              {t(`${EMAIL_NS}.receiving_help`)}
            </p>
            <CopyField
              label={t(`${EMAIL_NS}.address_label`)}
              value={params?.inbound_address ?? ''}
              testId="email-forward-target"
              copyTestId="email-forward-target-copy"
              onCopy={onAddressCopied}
            />

            {params?.forwarding_active ? (
              // Once a forwarded email has arrived the setup steps and the
              // "waiting" prompt are done: collapse to a confirmation.
              <p
                className="flex items-center gap-1.5 text-sm text-success"
                data-testid="email-forwarding-active"
              >
                <CheckCircle2 className="size-4" />
                {t(`${EMAIL_NS}.forwarding_active`)}
              </p>
            ) : (
              <>
                <div className="space-y-1.5">
                  <p className="text-sm font-medium text-foreground">
                    {t(`${EMAIL_NS}.forwarding_title`)}
                  </p>
                  <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground marker:text-foreground-subtle">
                    {forwardingSteps.map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                  </ol>
                </div>
                <p
                  className="flex items-center gap-1.5 text-sm text-muted-foreground"
                  data-testid="email-forwarding-pending"
                >
                  <Clock className="size-4" />
                  {t(`${EMAIL_NS}.forwarding_pending`)}
                </p>
              </>
            )}

            {!params?.forwarding_active && params?.forwarding_confirmation ? (
              <div
                className="rounded-lg border border-info-muted bg-info-muted/30 p-3"
                data-testid="email-forwarding-confirmation"
              >
                {params.forwarding_confirmation_value ? (
                  <CopyField
                    label={t(`${EMAIL_NS}.confirmation_title`)}
                    value={params.forwarding_confirmation_value}
                    testId="email-forwarding-confirmation-value"
                    copyTestId="email-forwarding-confirmation-copy"
                    description={t(`${EMAIL_NS}.confirmation_description`)}
                  />
                ) : (
                  <p
                    className="text-sm font-medium text-foreground"
                    data-testid="email-forwarding-confirmation-status"
                  >
                    {t(`${EMAIL_NS}.confirmation_title`)}
                  </p>
                )}
              </div>
            ) : null}
          </TabPanel>

          <TabPanel value="sending" className="space-y-4 pt-4" data-testid="email-branded-section">
            {!savedAddress ? (
              <p
                className="text-sm text-muted-foreground text-pretty"
                data-testid="email-sending-requires-address"
              >
                {t(`${EMAIL_NS}.sending_requires_address`)}
              </p>
            ) : isPublicDomain ? (
              <p
                className="text-sm text-muted-foreground text-pretty"
                data-testid="email-public-domain-note"
              >
                {t(`${EMAIL_NS}.public_domain_note`)}
              </p>
            ) : isDomainAuthenticated ? (
              <p
                className="flex items-center gap-1.5 text-sm text-success"
                data-testid="email-domain-verified"
              >
                <ShieldCheck className="size-4" />
                {t(`${EMAIL_NS}.domain_verified`)}
              </p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground text-pretty">
                  {t(`${EMAIL_NS}.dns_intro`)}
                </p>
                <a
                  href={DOMAIN_DOCS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-fit items-center gap-1 text-xs font-medium text-foreground hover:text-muted-foreground"
                >
                  {t(`${EMAIL_NS}.domain_docs_link`)}
                  <ArrowUpRight className="size-3.5" />
                </a>
                {dnsRecords.map((record) => (
                  <div
                    key={record.key}
                    className="space-y-2 rounded-lg border border-border p-3"
                    data-testid={`email-dns-${record.key}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-foreground-subtle uppercase">
                        {record.type}
                      </span>
                      {record.valid ? (
                        <span className="flex items-center gap-1 text-xs text-success">
                          <CheckCircle2 className="size-3.5" />
                          {t(`${EMAIL_NS}.dns_verified`)}
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="size-3.5" />
                          {t(`${EMAIL_NS}.dns_pending`)}
                        </span>
                      )}
                    </div>
                    <CopyField
                      label={t(`${EMAIL_NS}.dns_host`)}
                      value={record.host}
                      testId={`email-dns-host-${record.key}`}
                      copyTestId={`email-dns-host-copy-${record.key}`}
                    />
                    <CopyField
                      label={t(`${EMAIL_NS}.dns_value`)}
                      value={record.data}
                      testId={`email-dns-value-${record.key}`}
                      copyTestId={`email-dns-value-copy-${record.key}`}
                    />
                  </div>
                ))}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={onVerify}
                  // Also held while an address save is in flight: that save can swap
                  // or clear the domain this verification would report on.
                  disabled={isVerifying || isSavingReplyAddress}
                  data-testid="email-verify-button"
                >
                  {isVerifying ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="size-4" />
                  )}
                  {t(`${EMAIL_NS}.verify`)}
                </Button>
              </>
            )}
          </TabPanel>
        </Tabs>
      </div>
    </EmailDialogShell>
  );
};
